<template>
  <Modal :open="!!claim" @close="$emit('close')">
    <div class="space-y-6">
      <!-- Header -->
      <div class="text-center">
        <h2 class="text-lg font-semibold text-gray-900">
          <span v-if="claimTicketStatusOpen(claim)">{{ t("pause_title", { friendlyId: claim.friendlyId }) }}</span>
          <span v-else>{{ t("edit_title", { friendlyId: claim.friendlyId }) }}</span>
        </h2>
      </div>

      <!-- Reopening date section -->
      <div class="space-y-4">
        <div>
          <p class="block text-sm font-medium text-gray-700 mb-2">{{ t("reopening_date_label") }}</p>
          <div class="space-y-2">
            <label
              v-for="option in options"
              :key="option.key"
              :data-test="`option-${option.key}`"
              class="flex items-center gap-3 px-3 py-2 border rounded-md cursor-pointer"
              :class="selectedOption === option.key ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-300'"
            >
              <input
                v-model="selectedOption"
                type="radio"
                name="reopening-option"
                :value="option.key"
                class="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <span class="text-sm text-gray-900">{{ option.label }}</span>
              <span v-if="option.date" class="text-sm text-gray-500 ml-auto">{{ formatDate(option.date) }}</span>
            </label>
          </div>
        </div>

        <div v-if="selectedOption === 'custom'">
          <input
            id="reopening-date"
            v-model="customDate"
            data-test="custom-date-input"
            type="date"
            :min="minDate"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div class="flex justify-end space-x-4">
          <SecondaryButton data-test="close-button" @click="$emit('close')">{{ t("close_button") }}</SecondaryButton>
          <GenericButton data-test="confirm-button" @click="confirmPause" :disabled="!reopeningDate">
            {{ t("confirm_button") }}
          </GenericButton>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Modal, GenericButton, SecondaryButton } from "@estaly/ui";
import { mapGetters } from "vuex";

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

// Local formatting: toISOString() would shift the date by one day in timezones ahead of UTC.
const toDateString = (date) =>
  [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");

export default {
  components: { SecondaryButton, GenericButton, Modal },

  props: {
    claim: {
      type: Object,
      required: true,
    },
  },

  emits: ["close", "confirm"],

  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },

  data() {
    return {
      selectedOption: "",
      customDate: "",
    };
  },

  computed: {
    ...mapGetters("claims", ["claimTicketStatusOpen"]),

    today() {
      return new Date();
    },

    nextMonday() {
      const daysUntilMonday = (8 - this.today.getDay()) % 7 || 7;
      return addDays(this.today, daysUntilMonday);
    },

    options() {
      return [
        { key: "one_week", label: this.t("option_one_week"), date: addDays(this.today, 7) },
        { key: "one_month", label: this.t("option_one_month"), date: addMonths(this.today, 1) },
        { key: "tomorrow", label: this.t("option_tomorrow"), date: addDays(this.today, 1) },
        { key: "next_monday", label: this.t("option_next_monday"), date: this.nextMonday },
        { key: "custom", label: this.t("option_custom"), date: null },
      ];
    },

    minDate() {
      return toDateString(addDays(this.today, 1));
    },

    reopeningDate() {
      if (this.selectedOption === "custom") return this.customDate;

      const option = this.options.find((o) => o.key === this.selectedOption);
      return option ? toDateString(option.date) : "";
    },
  },

  methods: {
    formatDate(date) {
      return date.toLocaleDateString(this.locale, { weekday: "long", day: "numeric", month: "long" });
    },

    confirmPause() {
      if (!this.reopeningDate) return;

      this.$emit("confirm", {
        claim: this.claim,
        reopeningDate: this.reopeningDate,
      });
    },
  },
};
</script>

<i18n src="./PauseClaimModal.i18n.json"></i18n>
